import { Theme, css, useTheme } from '@emotion/react';

import { useRouter } from 'next/router';

import {
  Fragment,
  ReactNode,
  startTransition,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useMounted } from '@hooks/useMounted';
import { FontSizes, Spacing } from 'chatie-design-system';
import { isAfter } from 'date-fns';

import Badge, { useBadgePresets } from '@components/common/Badge';

import { safeArea, sticky } from '@styles/css';
import { ParsedStorage } from '@utils/localStorage';
import { CSSProps } from '@utils/misc';
import { changeQueryWithoutRendering } from '@utils/query';

const badgeCSS = css`
  border-radius: 8px;
  height: 14px;
  width: 14px;
  text-align: center;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  margin-left: 4px;
  align-self: center;
`;

const dateBadgeCSS = css`
  border-radius: 8px;
  height: 15px;
  width: 23px;
  text-align: center;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  margin-left: 4px;
  align-self: center;
`;

const badgeTabWrapperCSS =
  (isFit: boolean, hideBorder: boolean) => (theme: Theme) =>
    css`
      background-color: ${theme.var.white};
      ${!hideBorder && `border-bottom: 1px solid ${theme.border.light}`};
      overflow: auto;
      white-space: nowrap;
      z-index: 9;
      display: inline-flex;
      padding: ${Spacing.medium}px ${Spacing.small}px ${Spacing.xsmall}px
        ${Spacing.small}px;
      align-items: flex-start;
      gap: 10px;

      ${isFit &&
      css`
        display: flex;

        button {
          flex-basis: 0;
        }
      `}

      &::-webkit-scrollbar {
        display: none;
      }

      .tab-badge {
        height: 30px;
        padding: ${Spacing.xsmall}px ${Spacing.small}px;
        display: inline-flex;
        align-items: center;
        border-radius: 5px;
      }
      .active-tab-badge {
        border: none;
      }
      .unactivate-tab-badge {
        border: 1px solid ${theme.var.gray200};
      }
    `;

const tabWrapperCSS =
  (isFit: boolean, top: number, hideBorder: boolean) => (theme: Theme) =>
    css`
      ${top > 0
        ? sticky(top)
        : css`
            position: sticky;
            top: 0;
            ${safeArea('top')}
          `};

      background-color: ${theme.var.white};
      ${!hideBorder && `border-bottom: 1px solid ${theme.border.light}`};
      overflow: auto;
      white-space: nowrap;
      z-index: 9;

      .peek-tab {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
      }

      ${isFit &&
      css`
        display: flex;

        button {
          flex-basis: 0;
        }
      `}

      &::-webkit-scrollbar {
        display: none;
      }
    `;

const tabCSS = (isActive: boolean, isFit: boolean) => (theme: Theme) =>
  css`
    font-size: ${FontSizes.small}px;
    height: 42px;
    padding: 0 ${Spacing.medium}px;

    ${isFit &&
    css`
      flex-grow: 1;
    `}

    ${isActive
      ? css`
          color: ${theme.var.black};
          border-bottom: 2px solid ${theme.var.black};
          font-weight: 600;
        `
      : css`
          color: ${theme.var.gray500};
          border-bottom: 2px solid transparent;
        `}
  `;

export type ITab<T> = {
  label: string | ReactNode;
  value: T;
};

type Props<T = unknown> = {
  tabs: ITab<T>[];
  activeTab?: T;
  fit?: boolean;
  stickyAt?: number;
  onChange?: (tabName: T) => void;
  hideBorder?: boolean;
  type?: 'default' | 'badge';
  lastPeekTabViewDate?: Date;
};

const Tab = <T,>({
  className,
  tabs,
  activeTab,
  fit = false,
  stickyAt = 44,
  onChange,
  hideBorder = false,
  type = 'default',
  lastPeekTabViewDate,
}: Props<T> & CSSProps) => {
  const { new: newbadge } = useBadgePresets();
  const theme = useTheme();
  const randomNum = useMemo(
    () => Math.floor(Math.random() * (50 - 20) + 20),
    []
  );

  const [isShowRandomNumber, setIsShowRandomNumber] = useState(false);

  useEffect(() => {
    const lastDate = lastPeekTabViewDate
      ? new Date(lastPeekTabViewDate)
      : undefined;
    if (lastDate) {
      lastDate?.setHours(lastDate?.getHours() + 6);
    }
    const now = new Date();
    const isShowRandomNumber =
      activeTab !== 'home_peek' && (!lastDate || isAfter(now, lastDate));
    setIsShowRandomNumber(isShowRandomNumber);
  }, []);

  if (type === 'badge') {
    return (
      <div
        css={badgeTabWrapperCSS(fit, hideBorder)}
        className={`${className} ${activeTab}`}
      >
        {tabs.map((tab) => {
          return (
            <Badge
              className={`tab-badge ${
                tab.value === activeTab
                  ? 'active-tab-badge'
                  : 'unactivate-tab-badge'
              }`}
              text={tab.value as string}
              onClick={() => onChange?.(tab.value)}
              backgroundColor={
                tab.value === activeTab
                  ? theme.colors.main500
                  : theme.true.white
              }
              color={
                tab.value === activeTab ? theme.true.white : theme.true.black
              }
              radius={5}
              fontSize={14}
            />
          );
        })}
      </div>
    );
  }
  return (
    <div css={tabWrapperCSS(fit, stickyAt, hideBorder)} className={className}>
      {tabs.map((tab) => {
        if (tab.value === 'chat-library') {
          return (
            <button
              css={tabCSS(tab.value === activeTab, fit)}
              onClick={() => onChange?.(tab.value)}
              key={`tab-${tab.label}`}
            >
              {tab.label}
              <Badge text={'N'} {...newbadge} css={badgeCSS} />
            </button>
          );
        }

        if (tab.value === 'home_peek') {
          return (
            <Fragment key={`tab-${tab.label}`}>
              <button
                className="peek-tab"
                css={tabCSS(tab.value === activeTab, fit)}
                onClick={() => onChange?.(tab.value)}
              >
                {tab.label}
                {isShowRandomNumber && (
                  <Badge
                    text={`${randomNum}`}
                    {...newbadge}
                    css={dateBadgeCSS}
                  />
                )}
              </button>
            </Fragment>
          );
        }
        return (
          <button
            css={tabCSS(tab.value == activeTab, fit)}
            onClick={() => onChange?.(tab.value)}
            key={`tab-${tab.label}`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

export default Tab;

export const useTab = <T,>(
  tabs: ITab<T>[],
  options: {
    replace: boolean;
    withoutReRendering?: boolean;
    refresh?: boolean;
    onClickTab?: (tab: T) => void;
  } = {
    replace: false,
    withoutReRendering: false,
    refresh: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onClickTab: () => {},
  }
) => {
  const router = useRouter();
  const isMounted = useMounted();

  const { replace, withoutReRendering } = options;
  const currentTab = router.query.tab;

  const [lastPeekTabViewDate, setLastPeekTabViewDate] = useState<
    Date | undefined
  >(undefined);
  const [activeTab, setActiveTab] = useState(
    (currentTab as unknown as T) || (tabs[0].value as unknown as T)
  );

  const onChange = (tab: T) => {
    options?.onClickTab?.(tab);
    if (tab === 'home_peek') {
      const date = new Date();
      setLastPeekTabViewDate(date);
      ParsedStorage.setItem('lastPeekTabViewDate', date);
    }
    if (options?.refresh && tab === activeTab) {
      return window.location.reload();
    }
    startTransition(() => {
      setActiveTab(tab);
    });
  };

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    if (activeTab && replace && !withoutReRendering) {
      router.replace(
        { query: { ...router.query, tab: activeTab as unknown as string } },
        undefined,
        { shallow: true, scroll: false }
      );
    } else if (withoutReRendering) {
      changeQueryWithoutRendering({
        ...router.query,
        tab: activeTab as unknown as string,
      });
    }
  }, [activeTab]);

  return {
    tabs,
    activeTab,
    onChange,
    lastPeekTabViewDate,
    setLastPeekTabViewDate,
  };
};
